<template>
  <div id="site-tree">
    <div class="site-tree--header">
      <span class="site-tree--title">{{ clientTitle }}</span>
      <v-btn
        elevation="2"
        style="background-color: #EF4135; margin-right: 5px; border-radius: 2px"
        color="white"
        icon
        tile
        @click="showExportModal"
      >
        <v-icon>
          save_alt
        </v-icon>
      </v-btn>
      <v-btn
        v-if="isLaboratoryTechnician"
        color="secondary"
        class="siteTree-btn"
        :disabled="!site.active"
        :data-cy="dataCy.sites.siteTree.buttons.startWo"
        @click="onClickStartVisit"
      >
        {{ $t('forms.btn.start') }}
      </v-btn>
    </div>
    <v-row
      align="start"
      justify="start"
      class="m-4"
    >
      <v-btn
        color="secondary"
        class="siteTree-btn"
        :disabled="!hasPermission('nodes.children.view')"
        @click="displayAllNodes"
      >
        {{ treeExpandState }}
      </v-btn>
      <v-switch
        v-model="includeInactive"
        class="ml-2"
        style="margin-top: 0px"
        :label="$t('clients.labels.includeInactive')"
        @change="onInactiveStatsChange()"
      />
    </v-row>
    <v-progress-linear
      v-if="loading"
      indeterminate
    />
    <div v-else>
      <Tree
        :id="site.id"
        :key="siteTreeKey"
        :active="site.active"
        :nodeItem="site"
        :name="site.name"
        nodeType="Site"
        :site="site"
        :showAll="showAll"
        :nodes="site.children"
        @nodeDelete="onNodeDeleteConfirm"
        @nodeRestore="onNodeRestoreConfirm"
        @reloadTree="reloadTree"
        @nodeItemChanged="nodeItemChanged"
        @dragAndDropEnded="onDragAndDropEnded"
      />
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import EventBus from '@/utilities/event-bus';
import NodeForm from '@/components/nodes/NodeForm';
import ExportForm from '@/components/sites/ExportForm.vue';
import ImageForm from '@/components/nodes/ImageForm';
import ControlChart from '@/components/nodes/controlChart/Main';
import AddAnalysisForm from '@/components/component/AddAnalysisForm';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm';
import workOrderTypes from '@/utilities/enums/workOrderTypes';
import completionStatus from '@/constants/workOrderStatusChoices';
import nodeMixim from '@/mixins/NodeMixim';
import roles from '@/constants/roles';
import WorkOrderWorkOrderType from '@/components/forms/workOrder/WorkOrderTypesForm';
import { isPlainObject } from 'lodash';
import { sites } from '@/aq-data-cy.json';
import Http from '@/constants/httpCode';
import FlowMeterMixin from '@/mixins/FlowMeterMixin';
import addAnalysisFormTitle from '@/constants/addAnalysisFormTitle';

export default {
    name: 'SiteTree',
    mixins: [
        nodeMixim,
        FlowMeterMixin,
    ],
    data() {
        return {
            client: { ...this.$store.getters.client },
            site: { ...this.$store.getters.site },
            showAll: false,
            loading: true,
            siteTreeKey: 0,
            includeInactive: false,
        };
    },
    computed: {
        ...mapGetters([
            'currentMember',
            'userLang',
            'hasPermission',
            'hasRole',
        ]),
        clientTitle() {
            return `${this.client.name} [${this.client.accountNumber}]`;
        },
        treeExpandState() {
            return this.showAll ? this.$t('tree.collapseAll') : this.$t('tree.expandAll');
        },
        isLaboratoryTechnician() {
            return !this.hasRole(roles.LABORATORY_CHEMIST.value);
        },
        dataCy() {
            return {
                sites,
            };
        },
    },
    created() {
        this.getData();
        this.initBusEvents();
    },
    destroyed() {
        EventBus.$off('node-action');
        EventBus.$off('toggle-action');
        EventBus.$off('call-method-on-node');
    },
    methods: {
        async getData() {
            if (this.hasPermission('nodes.tree.view') && this.site.id) {
                const data = await this.getSiteTree();
                this.site = {
                    ...this.site,
                    children: this.mapChildren(data.tree),
                    showChildren: false,
                };
                if (!data.isAllAnalysisConfigured) {
                    this.$root.$emit('showModal', {
                        title: this.$t('sites.alert'),
                        component: Vue.component('ConfirmForm', ConfirmForm),
                        payload: {
                            msg: this.$t('sites.noAnalysisConfigured'),
                        },
                        isForm: false,
                    });
                }
            }
            this.loading = false;
            this.siteTreeKey = Math.random();
        },
        async getSiteTree(params) {
            const { data } = await this.$rq.getNodeTree(this.site.id, 'site', params || {
                includeInactive: this.includeInactive,
                checkCalculatedAnalysesConfigured: true,
            });
            if (!data.isAllAnalysisConfigured) {
                this.$root.$emit('showModal', {
                    title: this.$t('sites.alert'),
                    component: Vue.component('ConfirmForm', ConfirmForm),
                    payload: {
                        msg: this.$t('sites.noAnalysisConfigured'),
                    },
                    isForm: false,
                    destroy: false,
                    onConfirm: () => {
                        this.$root.$emit('destroyModal');
                    },
                });
            } else {
                this.$root.$emit('destroyModal');
            }
            return data;
        },
        onInactiveStatsChange() {
            this.getData();
        },
        async loadSiteNodes() {
            if (this.site.id) {
                const siteCopy = { ...this.site };
                const { tree: children } = await this.getSiteTree();
                const nodes = this.flatMapAllNodes(siteCopy.children);

                const childrenWithStatus = children.map(node => this.setChildrenShowStatus(node, nodes));
                this.site = {
                    ...this.site,
                    showChildren: true,
                    children: childrenWithStatus,
                };
            }
        },
        displayAllNodes() {
            this.showAll = !this.showAll;
            this.site.showChildren = this.showAll;
        },
        onClickStartVisit() {
            this.$root.$emit('showModal', {
                title: this.$t('workOrders.chooseworkOrderVisitSelection'),
                buttonTitle: 'confirm',
                clean: true,
                component: Vue.component('WorkOrderWorkOrderType', WorkOrderWorkOrderType),
                onConfirm: requestPayload => this.addWorkOrderTypesToWorkOrder(requestPayload),
            });
        },
        async addWorkOrderTypesToWorkOrder(requestPayload) {
            this.loading = true;
            const workOrder = {
                site: this.site.id,
                startDate: new Date(),
                type: workOrderTypes.VISIT,
                assignedTo: [this.currentMember.id],
                completionStatus: completionStatus.TODO,
                workOrderTypes: requestPayload ? requestPayload.selectedWorkOrdertypes : [],
            };

            const { data } = await this.$rq.addWorkOrder(workOrder);
            this.loading = false;

            await this.$router.push({
                name: 'WorkOrderView',
                params: {
                    workOrderId: data.id,
                    clientId: data.site.client.ref,
                    siteId: data.site.ref,
                },
            });
        },
        showExportModal() {
            this.$root.$emit('showModal', {
                title: this.$t('exportAnalysis.pageName'),
                showConfirmButton: false,
                component: Vue.component('ExportForm', ExportForm),
            });
        },
        handleAction(action, payload, disableNodeType = false) {
            switch (action) {
                case 'editContainer':
                case 'editNetwork':
                case 'editComponent':
                    this.$root.$emit('showModal', {
                        title: this.$t('sites.nodes.editNode'),
                        payload: {
                            parentNode: payload.parentNode,
                            siteId: payload.siteId,
                            populateWith: payload.node,
                            isEdition: true,
                            disableNodeType,
                            actionName: payload.actionName,
                        },
                        component: Vue.component('NodeForm', NodeForm),
                        onConfirm: requestPayload => this.editNodeRequest(requestPayload),
                    });
                    break;
                case 'addNode':
                    this.$root.$emit('showModal', {
                        title: this.$t('sites.nodes.addNode'),
                        payload: {
                            parentNode: Object.assign(
                                payload.node,
                                // eslint-disable-next-line max-len
                                payload.parentNode ? { parentNodeTypeId: payload.parentNode.nodeTypeId, parentNodeType: payload.parentNode.nodeType } : { empty: true },
                            ),
                            siteId: payload.siteId,
                        },
                        destroy: false,
                        component: Vue.component('nodeForm', NodeForm),
                        onConfirm: requestPayload => this.addNodeRequest(requestPayload),
                    });
                    break;
                case 'addAnalysis':
                    // eslint-disable-next-line no-case-declarations
                    let defaultTitle = this.$t('sites.nodes.addAnalysis');
                    if (payload.compTitle === addAnalysisFormTitle.editAnalysis) defaultTitle = this.$t('sites.nodes.addEditAnalysis');
                    if (payload.compTitle === addAnalysisFormTitle.addFlowmeter) defaultTitle = this.$t('sites.nodes.addEditFlowmeter');
                    if (payload.compTitle === addAnalysisFormTitle.addProductLevel) defaultTitle = this.$t('sites.nodes.addEditProductLevel');
                    if (payload.compTitle === addAnalysisFormTitle.addFuelConsumption) defaultTitle = this.$t('sites.nodes.addEditFuelConsumption');
                    this.$root.$emit('showModal', {
                        title: defaultTitle,
                        payload: {
                            node: Object.assign(payload.node, { siteId: payload.siteId }),
                            componentId: (payload.node.ref !== 'undefined') ? payload.node.ref : payload.node.id,
                            siteId: payload.siteId,
                            netWorkName: payload.parentNode.name,
                            showBreadcrumbs: payload.showBreadcrumbs,
                            itemValue: 'id',
                            compTitle: payload.compTitle,
                            actionName: payload.actionName,
                            isFromSiteTree: true,
                            type: payload.type,
                        },
                        component: Vue.component('addAnalysis', AddAnalysisForm),
                        // Handles both deletions and adds
                        // TODO AQ-7908: Refact AddMany and RemoveMany Analyses at same time
                        onConfirm: requestPayload => this.handleConfigurationRequest(requestPayload),
                    });
                    break;
                case 'removeConfiguration':
                    this.deleteConfigurationRequest(payload);
                    break;
                case 'nodeImage':
                    this.handleNodeImage(payload);
                    break;
                case 'controlChart':
                    this.handleControleChart(payload);
                    break;
                default:
            }
        },
        async handleNodeImage(payload) {
            this.$root.$emit('showModal', {
                title: this.$t('sites.nodes.addImage'),
                payload: {
                    node: Object.assign(payload.node),
                    nodeType: payload.nodeType,
                    netWorkName: payload.parentNode.name,
                    showBreadcrumbs: payload.showBreadcrumbs,
                    actionName: payload.actionName,
                },
                component: Vue.component('imageForm', ImageForm),
                isForm: false,
                onConfirm: requestPayload => {
                    if (requestPayload) {
                        this.addNodeImage(requestPayload, payload);
                    }
                },
            });
        },
        async addNodeImage({ files, fileToRemove = null }, { node }) {
            const payload = {
                id: node.ref,
                nodeType: node.nodeType,
                nodeTypeToRedirect: node.nodeType,
                files,
            };
            if (fileToRemove) {
                await this.$rq.deleteFile(fileToRemove);
            }

            await this.$rq.editNode(payload);
        },
        async addNodeRequest(requestPayload) {
            const { parentNode, siteId } = requestPayload;
            if (parentNode.empty) {
                parentNode.id = siteId;
                parentNode.nodeType = 'site';
            }

            const payload = this.$utils.removeEmptyNullOrUndefined(requestPayload.node, true);
            const { data } = await this.$rq.addNode(
                parentNode.ref || parentNode.id,
                parentNode.nodeType,
                payload,
            );

            this.$toasted.global.toast_success({
                message: this.$t('msg.nodeCreated', {
                    nodeType: data.nodeType,
                    nodeName: data.name,
                }),
            });

            await this.loadSiteNodes();
            EventBus.$emit('call-method-on-node', { nodeId: data.id, parentNodeId: parentNode.ref || parentNode.id, methodName: 'showNodeId' });
            await this.$utils.scrollToId(data.id);
        },
        async editNodeRequest(requestPayload) {
            if (requestPayload) {
                const { type } = requestPayload.node;

                const payload = {
                    // id and nodeType is used to redirect to proper endpoint
                    id: requestPayload.node.ref,
                    nodeType: requestPayload.node.nodeType,
                    nodeTypeToRedirect: requestPayload.node.nodeTypeToRedirect,
                    name: requestPayload.node.name,
                    description: requestPayload.node.description,
                    type: isPlainObject(type) ? type.id : type,
                    active: requestPayload.node.active,
                };
                const { data } = await this.$rq.editNode(payload);
                this.$toasted.global.toast_success({
                    message: this.$t('msg.nodeModified', {
                        nodeType: data.nodeType,
                        nodeName: data.name,
                    }),
                });
                await this.loadSiteNodes();
                await this.$utils.scrollToId(data.id);
            }
        },
        async handleConfigurationRequest(requestPayload) {
            if (!requestPayload || (!requestPayload.analyses.length && !requestPayload.firstLoadSelectedAnalysis.length)) {
                return;
            }

            const { analyses, componentId, firstLoadSelectedAnalysis, configurations } = requestPayload;
            const firstLoadSelectedAnalysisTypesMappedAnalysisConfigurations = configurations.reduce((acc, item) => ({
                ...acc,
                [item.analysisType.id]: item.id,
            }), {});

            const analysesToAdd = analyses.filter(analysis => !firstLoadSelectedAnalysis.includes(analysis));
            const analysesToDelete = firstLoadSelectedAnalysis.filter(analysis => !analyses.includes(analysis));
            const addPromises = [];
            analysesToAdd.forEach(analysis => addPromises.push(this.$rq.addComponentConfiguration(componentId, { analysisType: analysis })));
            const removePromises = [];
            analysesToDelete.forEach(analysis => removePromises.push(
                this.$rq.deleteComponentConfiguration(componentId, firstLoadSelectedAnalysisTypesMappedAnalysisConfigurations[analysis]),
            ));

            const [addedResponse] = await Promise.all(addPromises);
            const [removedResponse] = await Promise.all(removePromises);
            if ((addedResponse?.status === Http.CREATE) || (removedResponse?.status === Http.OK)) {
                if (addPromises.length) {
                    const addedConfigurations = this.getAnalysisNames(configurations, analysesToAdd);
                    this.$toasted.global.toast_success({
                        message: this.$t('msg.configurationAdded', { configuration: addedConfigurations }),
                    });
                }

                if (removePromises.length) {
                    const removedAnalysisName = this.getAnalysisNames(configurations, analysesToDelete);
                    this.$toasted.global.toast_success({
                        message: this.$t('msg.analysisRemoved', { data: removedAnalysisName }),
                    });
                }

                EventBus.$emit('call-method-on-node', {
                    nodeId: componentId,
                    methodName: 'showNodeId',
                });
                this.$utils.scrollToId(componentId);
            }
        },
        getAnalysisNames(configurations, analysesIds) {
            return configurations.flatMap(analysis => analysis.analysisType)
                .filter(an => analysesIds.includes(an.id))
                .map(an => an.name[this.userLang]).join(', ');
        },

        async deleteConfigurationRequest(requestPayload) {
            const { componentId, configurationId, configurationName } = requestPayload;
            await this.$rq.deleteComponentConfiguration(componentId, configurationId);

            this.$toasted.global.toast_success({
                message: this.$t('msg.analysisRemoved', { data: configurationName[this.userLang] }),
            });

            EventBus.$emit('call-method-on-node', {
                nodeId: componentId,
                methodName: 'showNodeId',
            });
            return this.$utils.scrollToId(componentId);
        },
        initBusEvents() {
            // Listen for the node from the tree that is getting clicked
            EventBus.$on('node-action', data => {
                const [action, item] = data;
                let disableNodeType = false;
                if (item?.nodeType === 'Network' && item?.node?.children?.length) {
                    // check if it only contains Components
                    if (item.node.children.every(child => child.nodeType === 'Component')) {
                        disableNodeType = true;
                    }
                }
                this.handleAction(action, item, disableNodeType);
            });
            EventBus.$on('toggle-action', async () => {
                await this.loadSiteNodes();
            });
        },
        async onNodeDeleteConfirm(siteId, nodes) {
            this.$root.$emit('showModal', {
                title: this.$t('forms.btn.deactivate'),
                component: Vue.component('ConfirmForm', ConfirmForm),
                payload: {
                    msg: this.$t('clientPage.removeSiteTreeNode'),
                    node: nodes.node,
                    netWorkName: nodes.parentNode.name,
                    showBreadcrumbs: nodes.showBreadcrumbs,
                },
                onConfirm: () => this.removeNode(nodes?.node?.ref, siteId),
                isForm: false,
            });
        },
        async removeNode(nodeId, siteId) {
            await this.$rq.deleteSiteNode(nodeId, siteId);
            await this.getData();
        },
        async onNodeRestoreConfirm(nodeId, siteId) {
            this.$root.$emit('showModal', {
                title: this.$t('clientPage.restoreSiteTreeNode'),
                component: Vue.component('ConfirmForm', ConfirmForm),
                payload: { msg: this.$t('clientPage.restoreSiteTreeNode') },
                onConfirm: () => this.restoreNode(nodeId, siteId),
                isForm: false,
            });
        },
        async restoreNode(nodeId, siteId) {
            await this.$rq.restoreSiteNode(nodeId, siteId);
            await this.getData();
        },
        async handleControleChart(node) {
            this.$root.$emit('showModal', {
                showNextPrevious: true,
                title: this.$t('controlChart.title'),
                payload: {
                    ...node,
                    netWorkName: node.parentNode.name,
                    showBreadcrumbs: node.showBreadcrumbs,
                    actionName: node.actionName,
                },
                component: Vue.component('controlChart', ControlChart),
                isForm: false,
                showSaveBtn: true,
                onConfirm: async requestPayload => {
                    if (requestPayload) {
                        await this.$rq.updateControlChart(node.node.ref, requestPayload);
                        await this.loadSiteNodes();
                    }
                },
            });
        },
        async reloadTree() {
            await this.loadSiteNodes();
        },
        nodeItemChanged(value) {
            const children = this.replaceNode(this.site, value);
            this.site = {
                ...this.site,
                children,
            };
            if (value.nodeType === 'Site') {
                this.site.showChildren = value.showChildren;
            }
        },
        onDragAndDropEnded() {
            this.reloadTree();
        },
    },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/utils/_mixins.scss";
@import "@/assets/styles/utils/_variables.scss";
@import '@/assets/styles/vuetify_variables.scss';

.site-tree--header {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: $primary-color;
    color: #fff;
    padding: 10px 16px;
    min-height: 64px;

    @media #{map-get($display-breakpoints, 'md-and-down')} {
        padding: 10px 10px;
    }
}

.site-tree--title {
    flex: 1;
    text-align: center;
    margin-right: 10px;
    font-size: 20px;
    @include lineClamp();

    @media #{map-get($display-breakpoints, 'md-and-down')} {
        text-align: left;
        font-size: 1rem;
    }
}
</style>
